import { useState } from 'react'
import { AlertDialog, Button, Flex, Text } from '@radix-ui/themes'

export interface ConfirmSpec {
  title: string
  description: string
  actionLabel: string
  pending?: boolean
  warning?: string | null
  errorMessage?: string | null
  onOpen?: () => void
}

// Shared open/confirm plumbing for both the dropdown-item and button confirm triggers.
export const useConfirmAction = (
  disabled: boolean,
  confirm: ConfirmSpec,
  onConfirm: () => void | Promise<void>
) => {
  const [open, setOpen] = useState(false)

  const handleOpenChange = (next: boolean) => {
    if (next && disabled) return
    setOpen(next)
    if (next) confirm.onOpen?.()
  }

  const handleClick = async () => {
    try {
      await onConfirm()
      setOpen(false)
    } catch {
      // keep dialog open; error renders from confirm.errorMessage
    }
  }

  return { open, handleOpenChange, handleClick }
}

export const ConfirmDialogContent = ({
  confirm,
  onConfirm
}: {
  confirm: ConfirmSpec
  onConfirm: () => void | Promise<void>
}) => (
  <AlertDialog.Content>
    <AlertDialog.Title>{confirm.title}</AlertDialog.Title>
    <AlertDialog.Description size="2">{confirm.description}</AlertDialog.Description>
    {confirm.warning ? (
      <Text color="amber" size="2" as="p" mt="3">
        {confirm.warning}
      </Text>
    ) : null}
    {confirm.errorMessage ? (
      <Text color="red" size="2" as="p" mt="3">
        {confirm.errorMessage}
      </Text>
    ) : null}
    <Flex gap="3" mt="4" justify="end">
      <AlertDialog.Cancel>
        <Button variant="soft" color="gray" disabled={confirm.pending}>
          Cancel
        </Button>
      </AlertDialog.Cancel>
      <Button onClick={onConfirm} loading={confirm.pending} disabled={confirm.pending}>
        {confirm.actionLabel}
      </Button>
    </Flex>
  </AlertDialog.Content>
)

interface ConfirmActionButtonProps {
  label: string
  disabled?: boolean
  variant?: 'solid' | 'soft'
  color?: 'gray' | 'orange'
  size?: '1' | '2' | '3'
  className?: string
  onConfirm: () => void | Promise<void>
  confirm: ConfirmSpec
}

export const ConfirmActionButton = ({
  label,
  disabled = false,
  variant = 'solid',
  color,
  size,
  className,
  onConfirm,
  confirm
}: ConfirmActionButtonProps) => {
  const { open, handleOpenChange, handleClick } = useConfirmAction(disabled, confirm, onConfirm)

  return (
    <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
      <AlertDialog.Trigger>
        <Button variant={variant} color={color} size={size} className={className} disabled={disabled}>
          {label}
        </Button>
      </AlertDialog.Trigger>
      <ConfirmDialogContent confirm={confirm} onConfirm={handleClick} />
    </AlertDialog.Root>
  )
}
